import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, Lock, Radio, Users, PlayCircle, CalendarDays } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LiveChat from '../components/LiveChat';
import api from '../lib/api';

const ACCESS = { public: 'Open to all', members: 'Members', paid: 'Paid members' };
const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return ''; } };

const RoomPlayer = ({ s }) => {
  const [p, setP] = useState(null);
  useEffect(() => {
    let ok = true;
    const load = () => api.get(`/live/room/${s.id}/playback`).then((r) => ok && setP((prev) => (prev && prev.uid && prev.uid === r.data.uid && prev.live === r.data.live ? prev : r.data))).catch((e) => ok && setP({ allowed: true, error: e?.response?.data?.detail }));
    load(); const t = setInterval(load, 20000);
    return () => { ok = false; clearInterval(t); };
  }, [s.id]);
  const box = (child) => <div className="w-full rounded-2xl bg-mahogany/90 text-cream flex flex-col items-center justify-center text-center p-6" style={{ aspectRatio: '16 / 9' }}>{child}</div>;
  if (!p) return box(<Loader2 className="w-6 h-6 animate-spin" />);
  if (!p.allowed) return box(<><Lock className="w-6 h-6 mb-2" /><p>{p.reason}</p><Link to={p.access === 'paid' ? '/membership' : '/login'} className="pill-btn mt-3" data-testid="room-gate-cta">{p.access === 'paid' ? 'See memberships' : 'Sign in'}</Link></>);
  if (p.error) return box(<p className="text-sm">{p.error}</p>);
  if (!p.src) return box(<><Radio className="w-6 h-6 mb-2" /><p>The stream hasn't started yet.</p><p className="text-xs opacity-70 mt-1">This page updates automatically.</p></>);
  return (
    <div className="relative w-full rounded-2xl overflow-hidden bg-black" style={{ aspectRatio: '16 / 9' }}>
      <iframe src={`${p.src}?autoplay=true&muted=true`} title={s.title} className="absolute inset-0 w-full h-full" frameBorder="0" allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowFullScreen data-testid={`room-player-${s.id}`} />
      {p.live && <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-600 text-white">Live</span>}
    </div>
  );
};

const Room = ({ s }) => (
  <div className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-5" data-testid={`room-${s.id}`}>
    <div className="min-w-0">
      <RoomPlayer s={s} />
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <h2 className="font-serif-display text-2xl text-burgundy">{s.title}</h2>
        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-rose-soft text-mahogany">{ACCESS[s.access] || 'Open to all'}</span>
      </div>
      {s.host_name && <div className="text-sm text-mahogany/70">with {s.host_name}</div>}
      {s.description && <p className="text-sm text-burgundy/80 mt-2">{s.description}</p>}
    </div>
    <LiveChat sessionId={s.id} hostUserId={s.owner_user_id} />
  </div>
);

const ListItem = ({ s, icon: Icon }) => (
  <Link to={`/members/live/${s.id}`} className="flex items-center gap-3 bg-white/70 rounded-xl border border-rose/40 px-4 py-3 hover:bg-white transition-colors" data-testid={`room-item-${s.id}`}>
    <Icon className="w-4 h-4 text-rose-italic shrink-0" />
    <span className="flex-1 min-w-0 truncate text-burgundy">{s.title}{s.host_name ? <span className="text-mahogany/60"> · {s.host_name}</span> : null}</span>
    <span className="text-xs text-mahogany/60 shrink-0">{s.scheduled_at ? fmt(s.scheduled_at) : ''}</span>
  </Link>
);

export default function MembersLive() {
  const { id } = useParams();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get('/live/room/sessions').then((r) => setItems(r.data || [])).catch(() => setItems([])); }, []);
  const now = Date.now();
  const list = items || [];
  const selected = list.find((s) => s.id === id) || list.find((s) => s.is_live)
    || [...list].filter((s) => s.scheduled_at && new Date(s.scheduled_at).getTime() > now).sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0];
  const upcoming = list.filter((s) => s !== selected && s.scheduled_at && new Date(s.scheduled_at).getTime() > now);
  const past = list.filter((s) => s !== selected && !upcoming.includes(s));
  return (
    <div className="App" data-testid="page-members-live">
      <Header />
      <main className="bg-cream min-h-[60vh]">
        <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16">
          <div className="eyebrow mb-3 inline-flex items-center gap-2"><Users className="w-4 h-4 text-rose-italic" /> Members&rsquo; Area</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-burgundy leading-tight">Members&rsquo; <span className="font-italic-display text-rose-italic">Live Room</span></h1>
          <p className="mt-4 text-mahogany/80 max-w-2xl">Satsangs and meditations streamed straight from Zoom to this room. Share reflections in the chat as you watch.</p>
          <div className="mt-10 space-y-10">
            {items === null ? <Loader2 className="w-6 h-6 animate-spin text-mahogany" />
              : !selected && !past.length ? <p className="text-mahogany/60" data-testid="room-empty">No sessions in the Members&rsquo; Room yet — check back soon.</p>
              : <>
                {selected && <Room s={selected} />}
                {upcoming.length > 0 && <div><h3 className="font-serif-display text-xl text-burgundy mb-3">Upcoming</h3><div className="space-y-2">{upcoming.map((s) => <ListItem key={s.id} s={s} icon={CalendarDays} />)}</div></div>}
                {past.length > 0 && <div><h3 className="font-serif-display text-xl text-burgundy mb-3">Past sessions</h3><div className="space-y-2">{past.map((s) => <ListItem key={s.id} s={s} icon={PlayCircle} />)}</div></div>}
              </>}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
